import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatedPageLoader } from 'smartsuite-ui';
import { getDataPromise } from 'brock-fetch-utils';
import { handleErrorResult } from '../viewDashboards/utils/fetchUtils';
import DashboardContext from '../shared/hooks/dashboardContext';
import CustomDashboard from './CustomDashboard';
import widgetDrawConfigurations from '../viewDashboards/widgets/widgetDrawConfigurations';
import { setupCustomDashboardConfiguration } from '../viewDashboards/utils/configurationAndValidationUtils';

export default function CustomDashboardLayout(props) {
    const { customizations, uriConstants } = useContext(DashboardContext);
    const [state, setState] = useState({});

    const setError = useCallback((error) => {
        setState((prevState) => ({ ...prevState, error }));
    }, []);

    useEffect(() => {
        function initialize() {
            const thresholdsPromise = getDataPromise(
                uriConstants.thresholdsUri
            );

            /* eslint-disable indent */
            const userDashboardConfigPromise = props.dashboardConfiguration
                ? undefined
                : getDataPromise(
                      uriConstants.getUserDashboardConfigurationUri(
                          props.dashboardKey
                      )
                  );
            /* eslint-enable indent */

            Promise.all([thresholdsPromise, userDashboardConfigPromise]).then(
                async ([thresholdValues, configuration]) => {
                    const widgetRenderers = {
                        ...widgetDrawConfigurations,
                        ...customizations.widgetRenderers,
                    };

                    const userDashboardConfig =
                        configuration ??
                        props.dashboardConfiguration ??
                        undefined;

                    const formattedConfiguration =
                        setupCustomDashboardConfiguration(
                            widgetRenderers,
                            userDashboardConfig
                        );

                    setState({
                        isLoaded: true,
                        configuration: formattedConfiguration,
                        thresholdValues,
                    });
                },
                (error) => handleErrorResult(error.httpResult, setError)
            );
        }

        initialize();
    }, [
        customizations.widgetRenderers,
        props.dashboardKey,
        props.dashboardConfiguration,
        uriConstants,
        setError,
    ]);

    function navigateToEditor(selectedWidgetId) {
        if (props.isCreateMode) {
            props.history.push({
                pathname: `Create`,
                state: {
                    selectedWidgetId,
                    dashboardConfiguration: JSON.stringify(
                        props.dashboardConfiguration
                    ),
                },
            });
        } else {
            props.history.push({
                pathname: `${props.dashboardKey}/Edit`,
                state: {
                    selectedWidgetId,
                    hasPendingChanges: props.hasPendingChanges,
                    dashboardConfiguration: JSON.stringify(
                        props.dashboardConfiguration
                    ),
                },
            });
        }
    }

    if (state.error) {
        throw Error(state.error);
    }

    if (!state.isLoaded) {
        return <AnimatedPageLoader />;
    }

    if (!state.configuration) {
        return null;
    }

    return (
        <CustomDashboard
            configuration={state.configuration}
            hasPendingChanges={props.hasPendingChanges}
            isCreateMode={props.isCreateMode}
            navigateToEditor={navigateToEditor}
            thresholdValues={state.thresholdValues}
        />
    );
}
CustomDashboardLayout.propTypes = {
    dashboardConfiguration: PropTypes.instanceOf(Object),
    dashboardKey: PropTypes.string.isRequired,
    hasPendingChanges: PropTypes.bool,
    history: PropTypes.instanceOf(Object).isRequired,
    isCreateMode: PropTypes.bool,
};
